import { MARD_291, PALETTE_NAME } from './palette';
import type { PatternProject } from '../types';

type CoordinateBead = [x: number, y: number, colorCode: string, completed: 0 | 1];

export interface YxpdDocument {
  format: 'yxpd';
  version: 1;
  coordinateBase: 1;
  palette: { id: 'MARD-291'; name: string; colors: Record<string, string> };
  project: {
    projectUid?: string;
    name: string;
    width: number;
    height: number;
    createdAt: number;
    updatedAt: number;
    sourceName: string;
    paletteBrand?: string;
    paletteName?: string;
    paletteCodeMap?: Record<string, string>;
  };
  beads: CoordinateBead[];
}

interface YxpdV2Envelope {
  format: 'yxpd'; version: 2; projectUid: string; exportUid: string; authorUid: string; authorName: string; exportedAt: number;
  algorithm: 'AES-256-GCM'; signatureAlgorithm: 'HMAC-SHA256'; keyVersion: number; iv: string; authTag: string; contentSha256: string; ciphertext: string; signature: string;
}

function safeName(value: string) {
  return value.replace(/[\\/:*?"<>|]/g, '_').trim() || '未命名图纸';
}

function download(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function collectBeads(project: PatternProject) {
  const beads: CoordinateBead[] = [];
  const colors: Record<string, string> = {};
  for (let index = 0; index < project.colorIndices.length; index += 1) {
    if (project.empty[index]) continue;
    const color = MARD_291[project.colorIndices[index]];
    if (!color) throw new Error('图纸中有无法识别的色号');
    const x = index % project.width + 1;
    const y = Math.floor(index / project.width) + 1;
    colors[color.code] = color.hex;
    beads.push([x, y, color.code, project.completed[index] ? 1 : 0]);
  }
  return { beads, colors };
}

export function serializeProjectDocument(project: PatternProject): YxpdDocument {
  const { beads, colors } = collectBeads(project);
  return {
    format: 'yxpd',
    version: 1,
    coordinateBase: 1,
    palette: { id: 'MARD-291', name: PALETTE_NAME, colors },
    project: {
      projectUid: project.projectUid,
      name: project.name,
      width: project.width,
      height: project.height,
      createdAt: project.createdAt,
      updatedAt: project.updatedAt,
      sourceName: project.sourceName,
      paletteBrand: project.paletteBrand,
      paletteName: project.paletteName,
      paletteCodeMap: project.paletteCodeMap
    },
    beads
  };
}

export async function exportProjectFile(project: PatternProject): Promise<string> {
  const document = serializeProjectDocument(project);
  const response = await fetch('/api/yxpd/export', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ projectUid: project.projectUid, document }) });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || '无损图纸加密失败');
  const envelope = data.envelope as YxpdV2Envelope;
  download(new Blob([JSON.stringify(envelope)], { type: 'application/x-yxpd+json' }), `${safeName(project.name)}.yxpd`);
  return envelope.exportUid;
}

export async function importProjectFile(file: File, ownerKey: string): Promise<PatternProject> {
  if (!/\.yxpd$/i.test(file.name)) throw new Error('请选择 .yxpd 格式的无损图纸');
  if (file.size > 30 * 1024 * 1024) throw new Error('图纸文件不能超过 30 MB');
  let parsed: YxpdDocument | YxpdV2Envelope;
  try {
    parsed = JSON.parse(await file.text()) as YxpdDocument | YxpdV2Envelope;
  } catch {
    throw new Error('这个文件不是有效的拼豆图纸');
  }
  if (parsed?.format === 'yxpd' && parsed.version === 2) {
    const response = await fetch('/api/yxpd/import', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ envelope: parsed }) });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || '图纸解密失败');
    return projectFromDocument(data.document as YxpdDocument, ownerKey, file.name, { projectUid: parsed.projectUid });
  }
  return projectFromDocument(parsed as YxpdDocument, ownerKey, file.name);
}

export function projectFromDocument(document: YxpdDocument, ownerKey: string, sourceName = '广场图纸', identity?: { id?: string; projectUid?: string; preserveTimes?: boolean }): PatternProject {
  if (document.format !== 'yxpd' || document.version !== 1 || document.coordinateBase !== 1) throw new Error('暂不支持这个图纸版本');
  const width = Math.floor(Number(document.project?.width));
  const height = Math.floor(Number(document.project?.height));
  if (width < 16 || width > 512 || height < 16 || height > 512) throw new Error('图纸尺寸超出 16～512 格范围');
  if (!Array.isArray(document.beads) || document.beads.length > width * height) throw new Error('图纸坐标数据不完整');
  const paletteIndex = new Map(MARD_291.map((color, index) => [color.code, index]));
  const colorIndices = new Uint16Array(width * height);
  const empty = new Uint8Array(width * height);
  empty.fill(1);
  const completed = new Uint8Array(width * height);
  const occupied = new Set<number>();
  for (const bead of document.beads) {
    if (!Array.isArray(bead) || bead.length < 3) throw new Error('图纸中有错误的坐标');
    const x = Math.floor(Number(bead[0]));
    const y = Math.floor(Number(bead[1]));
    const color = paletteIndex.get(String(bead[2]));
    if (x < 1 || x > width || y < 1 || y > height || color === undefined) throw new Error(`无法读取坐标 (${x}, ${y}) 的色号`);
    const index = (y - 1) * width + x - 1;
    if (occupied.has(index)) throw new Error(`坐标 (${x}, ${y}) 重复`);
    occupied.add(index);
    colorIndices[index] = color;
    empty[index] = 0;
    completed[index] = bead[3] ? 1 : 0;
  }
  const now = Date.now();
  return {
    id: identity?.id || crypto.randomUUID(),
    projectUid: identity?.projectUid || document.project.projectUid || `PRJ-${crypto.randomUUID()}`,
    ownerKey,
    name: String(document.project.name || sourceName.replace(/\.yxpd$/i, '') || '导入图纸').slice(0, 40),
    width,
    height,
    colorIndices,
    empty,
    completed,
    createdAt: identity?.preserveTimes ? Number(document.project.createdAt || now) : now,
    updatedAt: identity?.preserveTimes ? Number(document.project.updatedAt || now) : now,
    sourceName: String(document.project.sourceName || sourceName),
    elapsedMs: 0,
    paletteBrand: document.project.paletteBrand,
    paletteName: document.project.paletteName,
    paletteCodeMap: document.project.paletteCodeMap
  };
}
